import { useState, useEffect, useRef } from 'react'
import { useFormStore, useFormActions, useSigningSession } from '@hooks/useFormStore'
import { signingApi } from '@services/api'
import { Button } from '@components/UI/Button'
import styles from './Signing.module.css'

export function OtpVerifySection() {
  const { setStep, setSigningSession } = useFormActions()
  const signingSessionId = useFormStore((state) => state.signingSessionId)
  const signingSession = useSigningSession()
  
  const [otpValue, setOtpValue] = useState<string[]>(['', '', '', '', '', ''])
  const [countdown, setCountdown] = useState(60)
  const [canResend, setCanResend] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const timerRef = useRef<number | null>(null)

  // Countdown timer
  useEffect(() => {
    startCountdown()
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [])

  const startCountdown = () => {
    setCountdown(60)
    setCanResend(false)
    
    if (timerRef.current) {
      clearInterval(timerRef.current)
    }

    timerRef.current = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current)
          }
          setCanResend(true)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  // Format time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Maschera contatto per privacy
  const maskContact = (type: string, value: string) => {
    if (type === 'email') {
      const [user, domain] = value.split('@')
      if (!domain || user.length <= 2) return value
      return `${user[0]}${'*'.repeat(user.length - 2)}${user[user.length - 1]}@${domain}`
    } else {
      if (value.length <= 4) return value
      return `${'*'.repeat(value.length - 4)}${value.slice(-4)}`
    }
  }

  // Handle input change
  const handleInputChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return

    const newOtpValue = [...otpValue]
    newOtpValue[index] = value.slice(-1)
    setOtpValue(newOtpValue)
    setError(null)

    // Move to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  // Handle key down
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpValue[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    
    if (pastedData) {
      const newOtpValue = [...otpValue]
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newOtpValue[i] = pastedData[i]
      }
      setOtpValue(newOtpValue)
      
      // Focus last filled input or next empty
      const nextIndex = Math.min(pastedData.length, 5)
      inputRefs.current[nextIndex]?.focus()
    }
  }

  // Verify OTP
  const handleVerify = async () => {
    if (!signingSessionId) {
      setError('Sessione non valida')
      return
    }

    const code = otpValue.join('')
    if (code.length !== 6) {
      setError('Inserisci il codice completo')
      return
    }

    setIsVerifying(true)
    setError(null)

    try {
      await signingApi.verifyOtp(signingSessionId, code)
      
      // Aggiorna sessione
      const { session } = await signingApi.getSession(signingSessionId)
      setSigningSession(session)

      // Passa alla firma
      setStep('firma')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Codice non valido'
      setError(message)
      setOtpValue(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } finally {
      setIsVerifying(false)
    }
  }

  // Resend OTP
  const handleResend = async () => {
    if (!signingSessionId) return

    setIsResending(true)
    setError(null)

    try {
      await signingApi.requestOtp(signingSessionId)
      startCountdown()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore durante il reinvio'
      setError(message)
    } finally {
      setIsResending(false)
    }
  }

  const otpCode = otpValue.join('')
  const isComplete = otpCode.length === 6

  return (
    <div className={styles.section}>
      {/* Progress Steps */}
      <div className={styles.progressSteps}>
        <div className={`${styles.progressStep} ${styles.progressStepCompleted}`}>
          <div className={styles.progressNumber}>
            <i className="bi bi-check" />
          </div>
          <span className={styles.progressLabel}>Richiedi</span>
        </div>
        <div className={`${styles.progressLine} ${styles.progressStepCompleted}`} />
        <div className={`${styles.progressStep} ${styles.progressStepActive}`}>
          <div className={styles.progressNumber}>2</div>
          <span className={styles.progressLabel}>Verifica</span>
        </div>
        <div className={styles.progressLine} />
        <div className={styles.progressStep}>
          <div className={styles.progressNumber}>3</div>
          <span className={styles.progressLabel}>Firma</span>
        </div>
        <div className={styles.progressLine} />
        <div className={styles.progressStep}>
          <div className={styles.progressNumber}>4</div>
          <span className={styles.progressLabel}>Completa</span>
        </div>
      </div>

      {/* Card */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardIcon}>
            <i className="bi bi-shield-lock" />
          </div>
          <h2 className={styles.cardTitle}>Inserisci il codice</h2>
          <p className={styles.cardDescription}>
            Abbiamo inviato un codice a 6 cifre a{' '}
            <strong>
              {signingSession ? maskContact(signingSession.contact_type, signingSession.contact_value) : '...'}
            </strong>
          </p>
        </div>

        <div className={styles.cardContent}>
          {/* OTP Input */}
          <div className={styles.otpInputContainer} onPaste={handlePaste}>
            {otpValue.map((digit, index) => (
              <input
                key={index}
                ref={(el) => { inputRefs.current[index] = el }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleInputChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={styles.otpInput}
                autoFocus={index === 0}
                disabled={isVerifying}
              />
            ))}
          </div>

          {/* Countdown or Resend */}
          {!canResend ? (
            <div className={styles.countdown}>
              <p className={styles.countdownLabel}>
                Tempo rimanente per inserire il codice
              </p>
              <p className={styles.countdownTime}>{formatTime(countdown)}</p>
            </div>
          ) : (
            <div className={styles.resendSection}>
              <p style={{ fontSize: '0.875rem', color: 'var(--color-neutral-500)', marginBottom: '0.5rem' }}>
                Non hai ricevuto il codice?
              </p>
              <button
                type="button"
                className={styles.resendButton}
                onClick={handleResend}
                disabled={isResending}
              >
                {isResending ? (
                  <i className="bi bi-arrow-repeat" style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <i className="bi bi-arrow-clockwise" />
                )}
                Invia nuovo codice
              </button>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className={styles.testModeNotice} style={{ background: 'linear-gradient(145deg, #fee2e2 0%, #fef2f2 100%)', borderColor: '#fca5a5' }}>
              <p style={{ color: '#991b1b' }}>
                <i className="bi bi-exclamation-triangle" style={{ marginRight: '0.5rem' }} />
                {error}
              </p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleVerify}
            disabled={!isComplete || isVerifying}
          >
            {isVerifying ? (
              <>
                <i className="bi bi-arrow-repeat" style={{ animation: 'spin 1s linear infinite' }} />
                Verifica in corso...
              </>
            ) : (
              'Verifica e prosegui'
            )}
          </button>

          {/* Security Footer */}
          <div className={styles.securityFooter}>
            <i className="bi bi-lock" />
            <span>Processo di verifica sicuro</span>
          </div>

          {/* Test mode notice removed for production */}
        </div>
      </div>

      {/* Navigation */}
      <div className={styles.actions}>
        <Button
          type="button"
          variant="secondary"
          icon="arrow-left"
          onClick={() => setStep('otp_request')}
          disabled={isVerifying}
        >
          Indietro
        </Button>
      </div>
    </div>
  )
}

